/**
 * DEFCAT P5 — two-axis catalogue read (lyse@7.6.0+).
 *
 *   GET /defect-catalogues/{id}?axis=all&includeValidity=true
 *
 * Returns BOTH the location tree and the type tree (each entry carrying the
 * two-axis fields: axis / parentEntryId / componentGroupId /
 * globalLocationDetailId / abbreviation / defaultDescription / isLeaf / depth /
 * defaultSeverityByModel), plus the node-level `validity[]` overlay and the
 * per-axis `axisLevels[]` labels.
 *
 * This is a SEPARATE hook from Story-033's `useDefectCatalogueQuery` (which
 * fetches the flat, back-compat `entry_type='defect_type'` response the
 * catalogue popover + favourites rely on). Keeping them apart means the
 * two-axis consumers (drawer location picker, severity prefill, collection
 * narrowing, the P6 admin editor) get the rich shape without disturbing the
 * flat consumers. The drawer reads this query's `data` directly — no store
 * bridge — so the flat cataloguesStore is untouched.
 *
 * SYNC: backend-requirement lyse-7.9.0-defcat.
 */
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import type { GetDefectCatalogueV2Response } from "@aeriosiq/types";

const STALE_TIME_MS = 5 * 60_000;
const GC_TIME_MS = 10 * 60_000;

export const catalogueV2Keys = {
  all: ["catalogues", "v2"] as const,
  detail: (catalogueId: string) =>
    [...catalogueV2Keys.all, catalogueId] as const,
};

export function useDefectCatalogueV2Query(catalogueId: string | null) {
  const api = useApi();
  return useQuery<GetDefectCatalogueV2Response>({
    queryKey: catalogueV2Keys.detail(catalogueId ?? ""),
    enabled: catalogueId !== null && catalogueId.length > 0,
    queryFn: async () => {
      const safeId = catalogueId ?? "";
      const res = await api.get<GetDefectCatalogueV2Response>(
        `/defect-catalogues/${encodeURIComponent(safeId)}?axis=all&includeValidity=true`,
      );
      return res.data;
    },
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    retry: 1,
    refetchOnWindowFocus: true,
    meta: { failureToast: "Catalogue structure unavailable" },
  });
}
